import React, { useCallback, useRef, useState } from "react";
import "./pedal_profile_modal.css";
import {
  applyPedalCurve,
  createLinearCurve,
  getCurveMaxOutput,
  type PedalCurvePoint,
  type PedalKind,
} from "../../utils/pedal_profile";
import {
  resetPedalProfile,
  setPedalCurve,
  updatePedalCurvePoint,
  usePedalProfile,
} from "../../hooks/directControl/pedal_profile_store";

interface PedalProfileModalProps {
  open: boolean;
  onClose: () => void;
  /** 현재 가속 페달 입력(0..1) — 라이브 마커로 표시(선택). */
  livePedal?: number;
  /** 현재 브레이크 입력(0..1) — 라이브 마커로 표시(선택). */
  liveBrake?: number;
  /** 제어 중에는 편집을 막는다(오조작 방지). */
  readOnly?: boolean;
}

// SVG 좌표계 — x/y 0..1 을 그래프 박스에 매핑.
const BOX = { w: 260, h: 200, pad: 18 };
const px = (x: number) => BOX.pad + x * (BOX.w - BOX.pad * 2);
const py = (y: number) => BOX.h - BOX.pad - y * (BOX.h - BOX.pad * 2);
const toX = (clientX: number, rect: DOMRect) =>
  (clientX - rect.left - BOX.pad) / (BOX.w - BOX.pad * 2);
const toY = (clientY: number, rect: DOMRect) =>
  1 - (clientY - rect.top - BOX.pad) / (BOX.h - BOX.pad * 2);

const PRESETS: Record<string, number[]> = {
  // y 값(5개) — x 는 0/0.25/0.5/0.75/1 고정.
  선형: [0, 0.25, 0.5, 0.75, 1],
  부드럽게: [0, 0.08, 0.25, 0.55, 1],
  민감하게: [0, 0.45, 0.7, 0.88, 1],
};

function CurveEditor(props: {
  kind: PedalKind;
  title: string;
  curve: PedalCurvePoint[];
  live?: number;
  readOnly?: boolean;
}) {
  const { kind, title, curve, live, readOnly } = props;
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const moveTo = useCallback(
    (index: number, clientY: number) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      updatePedalCurvePoint(kind, index, toY(clientY, rect));
    },
    [kind],
  );

  const onPointerDown = (index: number) => (e: React.PointerEvent) => {
    if (readOnly || index === 0) return; // 첫 점은 0 고정
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDragIndex(index);
    moveTo(index, e.clientY);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (dragIndex == null) return;
    moveTo(dragIndex, e.clientY);
  };

  const endDrag = () => setDragIndex(null);

  const path = curve
    .map((p, i) => `${i === 0 ? "M" : "L"}${px(p.x).toFixed(1)},${py(p.y).toFixed(1)}`)
    .join(" ");
  const maxOutput = getCurveMaxOutput(curve);
  const liveY = live != null ? applyPedalCurve(curve, live) : null;

  return (
    <div className="pp-editor" data-testid={`pedal-profile-${kind}`}>
      <div className="pp-editor__head">
        <span className="pp-editor__title">{title}</span>
        <span className="pp-editor__max" data-testid={`pedal-profile-${kind}-max`}>
          최고 {Math.round(maxOutput * 100)}%
        </span>
      </div>
      <svg
        ref={svgRef}
        className="pp-graph"
        viewBox={`0 0 ${BOX.w} ${BOX.h}`}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        {/* 격자 + 기준(선형) */}
        {[0.25, 0.5, 0.75].map((g) => (
          <g key={g}>
            <line className="pp-grid" x1={px(g)} y1={py(0)} x2={px(g)} y2={py(1)} />
            <line className="pp-grid" x1={px(0)} y1={py(g)} x2={px(1)} y2={py(g)} />
          </g>
        ))}
        <line className="pp-ref" x1={px(0)} y1={py(0)} x2={px(1)} y2={py(1)} />
        <rect
          className="pp-frame"
          x={px(0)}
          y={py(1)}
          width={px(1) - px(0)}
          height={py(0) - py(1)}
        />
        <path className="pp-curve" d={path} />
        {/* 라이브 입력 위치 */}
        {liveY != null && live! > 0.01 ? (
          <g className="pp-live">
            <line x1={px(live!)} y1={py(0)} x2={px(live!)} y2={py(liveY)} />
            <circle cx={px(live!)} cy={py(liveY)} r={4} />
          </g>
        ) : null}
        {/* 제어점 */}
        {curve.map((p, i) => (
          <circle
            key={i}
            className={`pp-point ${i === 0 ? "is-fixed" : ""} ${dragIndex === i ? "is-drag" : ""}`}
            data-testid={`pedal-profile-${kind}-point-${i}`}
            cx={px(p.x)}
            cy={py(p.y)}
            r={i === 0 ? 4 : 7}
            onPointerDown={onPointerDown(i)}
          />
        ))}
      </svg>
      <div className="pp-presets">
        {Object.entries(PRESETS).map(([name, ys]) => (
          <button
            key={name}
            type="button"
            className="pp-preset"
            disabled={readOnly}
            data-testid={`pedal-profile-${kind}-preset-${name}`}
            onClick={() =>
              setPedalCurve(
                kind,
                createLinearCurve().map((p, i) => ({ ...p, y: ys[i] })),
              )
            }
          >
            {name}
          </button>
        ))}
      </div>
    </div>
  );
}

function PedalProfileModalComponent(props: PedalProfileModalProps) {
  const { open, onClose, livePedal, liveBrake, readOnly } = props;
  const profile = usePedalProfile();

  if (!open) {
    return null;
  }

  return (
    <div className="pp-overlay" data-testid="pedal-profile-modal" onClick={onClose}>
      <div
        className="pp-modal"
        role="dialog"
        aria-modal="true"
        aria-label="페달 프로파일 설정"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pp-head">
          <div>
            <div className="pp-eyebrow">직접 제어 · 콕핏 공통</div>
            <h3 className="pp-title">페달 프로파일</h3>
            <p className="pp-sub">
              페달을 밟은 정도(가로)에 대해 실제로 나갈 출력(세로)을 정합니다.
              점을 위아래로 끌어 조절하고, <b>맨 오른쪽 점이 최고값</b>입니다.
            </p>
          </div>
          <button
            type="button"
            className="pp-close"
            data-testid="pedal-profile-close"
            aria-label="닫기"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {readOnly ? (
          <div className="pp-readonly" data-testid="pedal-profile-readonly">
            제어 중에는 수정할 수 없습니다. 제어를 종료한 뒤 조절해 주세요.
          </div>
        ) : null}

        <div className="pp-body">
          <CurveEditor
            kind="accel"
            title="가속"
            curve={profile.accel}
            live={livePedal}
            readOnly={readOnly}
          />
          <CurveEditor
            kind="brake"
            title="브레이크"
            curve={profile.brake}
            live={liveBrake}
            readOnly={readOnly}
          />
        </div>

        <div className="pp-foot">
          <span className="pp-hint">
            페달을 떼면 항상 0, 곡선은 항상 증가하도록 자동 보정됩니다.
          </span>
          <button
            type="button"
            className="pp-reset"
            disabled={readOnly}
            data-testid="pedal-profile-reset"
            onClick={resetPedalProfile}
          >
            초기화
          </button>
        </div>
      </div>
    </div>
  );
}

export default React.memo(PedalProfileModalComponent);
